import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Plus } from '../../../assets/icons/plus.js';
import CustomTable from '../../../components/common/CustomTable/index.jsx';
import { useSelection } from '../../../hooks/use-selection.js';
import { columns } from './columns.js';
import { styles } from './styles.js';
import { onPageChange, onPageSizeChange, onSortChange } from '../../../store/slices/tableController.js';
import PermissionChecker from '../../../components/common/PermissionChecker/index.js';
import { useListReviewPoliciesMutation } from '../../../api/preHQ';
import { hasPermission } from '../../../utils/permission.jsx';

const ChangeRequestPolicies = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const controller = useSelector((state) => state.tableController);
    const currentUser = useSelector((state) => state.session.user);
    const [crPoliciesData, setCrPoliciesData] = useState();

    const [listReviewPolicies, { isLoading: isListReviewLoading }] = useListReviewPoliciesMutation();
    const isLoading = isListReviewLoading;

    const [selectedPositions, handleSelect, handleSelectAll] = useSelection(crPoliciesData?.changeRequestPolicies);

    const fetchData = useCallback(
        async (filters, sort, sortBy, page, pageSize) => {
            try {
                const formData = new FormData();
                if (filters.length) {
                    formData.append('c', filters[0].property);
                    formData.append('q', filters[0].value);
                }
                if (sortBy) {
                    formData.append('sort', sortBy);
                    formData.append('order', sort);
                }
                formData.append('offset', page * pageSize);
                formData.append('max', pageSize);

                const response = await listReviewPolicies(formData).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                setCrPoliciesData(response);
            } catch (error) {
                if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
                else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
            }
        },
        [listReviewPolicies],
    );

    useEffect(() => {
        if (fetchData)
            fetchData(controller.filters, controller.sort, controller.sortBy, controller.page, controller.pageSize);
    }, [controller.filters, controller.sort, controller.sortBy, controller.page, controller.pageSize, fetchData]);

    const handlePageChange = (newPage) => {
        dispatch(onPageChange(newPage - 1));
    };

    const handlePageSizeChange = (newPageSize) => {
        dispatch(onPageSizeChange(newPageSize));
    };

    const handleSortChange = (event, property) => {
        const isAsc = controller.sortBy === property && controller.sort === 'asc';
        dispatch(
            onSortChange({
                sort: isAsc ? 'desc' : 'asc',
                sortBy: property,
            }),
        );
    };

    const handleRowClick = (row) => {
        navigate(`/client/reviewrequest/reviewpolicies/view/${row}`);
    };

    const hasViewPermission = useMemo(
        () => hasPermission(currentUser.authorities, ['ROLE_BASE_PERMISSION_PRE_HQ_VIEW_REVIEW_POLICY']),
        [currentUser.authorities],
    );

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Review Policy</Typography>
                <PermissionChecker
                    requiredPermissions={['ROLE_BASE_PERMISSION_PRE_HQ_CREATE_REVIEW_POLICY']}
                    noAuthRedirect={false}
                >
                    <Button
                        color="primary"
                        onClick={() => navigate('/client/reviewrequest/reviewpolicies/add')}
                        size="large"
                        startIcon={<Plus fontSize="small" />}
                        variant="contained"
                    >
                        Add
                    </Button>
                </PermissionChecker>
            </Box>
            <Card sx={styles.contentBodyWrapper}>
                <CardContent>
                    <CustomTable
                        isLoading={isLoading}
                        onRowClick={hasViewPermission && handleRowClick}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onSelect={handleSelect}
                        onSelectAll={handleSelectAll}
                        onSortChange={handleSortChange}
                        page={controller.page + 1}
                        columns={columns}
                        rows={crPoliciesData?.reviewPolicies || []}
                        rowsCount={crPoliciesData?.reviewPolicyCount || 0}
                        selectedRows={selectedPositions}
                        sort={controller.sort}
                        sortBy={controller.sortBy}
                        showRowSelector={false}
                        showPagignation
                        keyColumn="id"
                        route="/client/reviewrequest/reviewpolicies"
                        action={() =>
                            fetchData(
                                controller.filters,
                                controller.sort,
                                controller.sortBy,
                                controller.page,
                                controller.pageSize,
                            )
                        }
                    />
                </CardContent>
            </Card>
        </Box>
    );
};

export default ChangeRequestPolicies;
